<template>
    <div class="min-h-screen bg-slate-50 flex flex-col">
        <!-- White navbar with border and profile dropdown (logout only) -->
        <header class="w-full bg-white border-b border-slate-200">
            <div
                class="px-4 md:px-8 py-4 flex items-center justify-between relative"
            >
                <div
                    class="inline-flex items-center gap-2"
                    aria-label="My10x logo"
                >
                    <img src="/images/logo.svg" alt="My10x" class="h-6" />
                </div>
                <div
                    class="relative inline-flex"
                    v-click-outside="closeAccountDropdown"
                >
                    <button
                        class="inline-flex justify-center items-center group"
                        aria-haspopup="true"
                        @click.prevent="accountOpen = !accountOpen"
                        :aria-expanded="accountOpen"
                    >
                        <div class="flex items-center truncate">
                            <span
                                class="truncate ml-2 text-sm font-medium group-hover:text-gray-800"
                            >
                                {{ userName }}
                            </span>
                            <svg
                                class="w-3 h-3 shrink-0 ml-1 fill-current text-gray-400"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z"
                                />
                            </svg>
                        </div>
                    </button>
                    <div
                        class="absolute right-0 top-full mt-1 z-10 flex w-[123px] p-2 items-start rounded-md border border-slate-200 bg-white shadow-sm"
                        v-show="accountOpen"
                    >
                        <ul class="flex items-start w-full">
                            <li class="w-full">
                                <a
                                    href="/logout"
                                    @click.prevent="logout()"
                                    class="flex w-full flex-1 text-slate-800 text-sm font-normal leading-5 px-1 py-1"
                                >
                                    Log out
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </header>

        <!-- Body (recycle Quiz style) -->
        <main class="flex-1 w-full flex items-center justify-center px-4">
            <div
                class="w-full max-w-[480px] bg-white rounded-2xl shadow px-10 pt-6 pb-12 flex flex-col items-center border border-slate-200"
            >
                <!-- Icon circle with 24px padding bottom below -->
                <div class="w-full flex flex-col items-center pb-6">
                    <div
                        class="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center"
                    >
                        <Icon
                            icon="mail"
                            :width="32"
                            :height="32"
                            pathClass="fill-orange"
                        />
                    </div>
                </div>
                <!-- Copy block -->
                <div
                    class="flex flex-col items-center gap-2 w-full max-w-[360px]"
                >
                    <div
                        class="self-stretch text-slate-800 text-center text-xl font-semibold leading-7"
                    >
                        Verify your email to continue
                    </div>
                    <p
                        class="self-stretch text-slate-800 text-center text-sm leading-5"
                    >
                        We need to confirm your email before you can keep using
                        your account. A message was sent to
                        <span class="font-medium">{{ userEmail }}</span>
                        .
                        <br />
                        No email? Check spam or resend.
                    </p>
                </div>
                <!-- Button block (24px top padding) -->
                <div class="w-full pt-6 flex flex-col items-center gap-3">
                    <button
                        type="button"
                        class="w-full px-4 py-[10px] rounded-md bg-[#FD421D] text-white text-sm font-medium leading-5 shadow-sm disabled:opacity-50"
                        :disabled="loading"
                        @click="resend"
                    >
                        Resend email
                    </button>
                </div>
            </div>
        </main>
        <AppNotifications />
    </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { usePage, router } from '@inertiajs/vue3';
import { notify } from 'notiwind';
import Plain from '@/Layouts/Plain.vue';
import AppNotifications from '@/components/app-notifications.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Auth from '@/services/auth';
import EventBus from '@/services/event-bus';

const page = usePage();
const user = computed(() => page.props.userData || {});
const userName = computed(() => user.value?.name || '');
const userEmail = computed(() => user.value?.email || '');
const verified = computed(() => !!user.value?.email_verified);
const deadlineAt = computed(
    () => page.props?.security?.email_deadline_at || null
);

const loading = ref(false);

// Navbar dropdown state/handlers (reuse behavior from Header.vue)
const accountOpen = ref(false);
const closeAccountDropdown = () => {
    accountOpen.value = false;
};
// Component-scoped refs for interval and popstate handler
const verifyInterval = ref(null);
const popHandlerRef = ref(null);

const logout = () => {
    const req = Auth.logout();
    req.then(() => {
        EventBus.emit('user-logged-out');
        localStorage.removeItem('_token');
        location.href = '/login';
    }).catch((err) => {
        try {
            console.error('[VerifyBlock] Logout failed', err);
        } catch (_) {}
        notify(
            {
                group: 'error',
                title: 'Logout failed',
                text: 'Unable to log out. Please try again.',
            },
            4000
        );
    });
};

// Use Plain layout to avoid Auth slider; matches minimal Figma
defineOptions({ layout: Plain });

function redirectIfNotBlocked() {
    try {
        const d = deadlineAt.value
            ? new Date(deadlineAt.value).getTime()
            : null;
        const now = Date.now();

        if (verified.value) {
            router.visit('/dashboard', { replace: true });
            return true;
        }
    } catch (_) {}
    return false;
}

onMounted(() => {
    // Defensive: if arriving here but no longer blocked, bounce back
    redirectIfNotBlocked();

    // Prevent back navigation escape; server middleware also enforces
    const onPop = (e) => {
        e.preventDefault();
        router.visit('/verify-block', { replace: true });
    };
    window.addEventListener('popstate', onPop);
    popHandlerRef.value = onPop;

    // Keep checking every 30s in case state changes (e.g., verification completes)
    // Clear existing just in case
    if (verifyInterval.value)
        try {
            clearInterval(verifyInterval.value);
        } catch (_) {}
    const t = setInterval(() => {
        if (verified.value) {
            router.visit('/dashboard', { replace: true });
        }
    }, 30000);
    verifyInterval.value = t;
});

onBeforeUnmount(() => {
    try {
        if (verifyInterval.value) clearInterval(verifyInterval.value);
    } catch (_) {}
    try {
        if (popHandlerRef.value)
            window.removeEventListener('popstate', popHandlerRef.value);
    } catch (_) {}
});

function resend() {
    loading.value = true;
    router.post(
        '/email/verification-notification',
        {},
        {
            onSuccess() {
                notify(
                    {
                        group: 'success',
                        title: 'Email sent',
                        text: `Email was sent to ${userEmail.value}`,
                    },
                    4000
                );
            },
            onError() {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Unable to resend verification email. Please try again in a moment.',
                    },
                    4000
                );
            },
            onFinish() {
                loading.value = false;
            },
        }
    );
}
</script>
